import React, { useContext, useEffect, useState } from 'react';
import useRouteSegmentService from '../../../app/services/hooks/useRouteSegmentService';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import { Place } from '../../../app/models/Place';
import { Unit } from '../../../app/models/Unit';
import { Currency } from '../../../app/models/Currency';
import {
    defaultRouteSegmentDataForm,
    RouteSegmentDataForm
} from '../../../app/models/RouteSegment';
import { AppContext } from '../../../contexts/AppContext';
import { toast } from 'react-toastify';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';
import LazyLoading from '../../../components/LazyLoading';
import RouteSegmentForm from './forms/RouteSegmentForm';
import ButtonCancelForm from '../../../components/buttons/ButtonCancelForm';
import ButtonSaveForm from '../../../components/buttons/ButtonSaveForm';
import { Toll } from '../../../app/models/Toll';

interface Props {
    onSaved?: () => void;
    onCancel?: () => void;
    onError?: () => void;
}

const RouteSegmentCreate = ({ onSaved, onCancel, onError }: Props) => {
    const { showLoading, hideLoading, changeAnimation } = useContext(AppContext);
    const [currencies, setCurrencies] = useState<Currency[]>([]);
    const [units, setUnits] = useState<Unit[]>([]);
    const [origins, setOrigins] = useState<Place[]>([]);
    const [destinations, setDestinations] = useState<Place[]>([]);
    const [tolls, setTolls] = useState<Toll[]>([]);
    const [errorGetData, setErrorGetData] = useState('');
    const [routeSegment, setRouteSegment] = useState<RouteSegmentDataForm>(
        defaultRouteSegmentDataForm
    );

    const [errorFields, setErrorFields] = useState<any>();

    const {
        fetchingStoreRouteSegment,
        storeRouteSegment,
        fetchingCreateRouteSegment,
        createRouteSegment
    } = useRouteSegmentService();

    useEffect(() => {
        if (showLoading) showLoading('loading', 'Cargando datos...');
        createRouteSegment({
            onSuccess: (response: ServiceResponse) => {
                setOrigins(response.data.origins);
                setDestinations(response.data.destinations);
                setTolls(response.data.tolls);
                setCurrencies(response.data.currencies);
                setUnits(response.data.units);

                if (hideLoading) hideLoading();
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                toast.error(response.message);
                setErrorGetData(response.message);
                if (onError) onError();
            }
        });
    }, []);

    const store = () => {
        if (showLoading) showLoading('loading', 'Guardando tramo...');
        storeRouteSegment(routeSegment, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();

                useReactConfirmAlert().successAlert({
                    title: 'Éxito',
                    message: response.message
                });
                toast.success(response.message, {
                    autoClose: 2500
                });
                if (onSaved) onSaved();
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                useReactConfirmAlert().errorAlert({
                    title: 'Error',
                    message: response.message
                });
            },
            onFieldError: (errorFields: ServiceResponse) => {
                if (hideLoading) hideLoading();

                setErrorFields(errorFields.data);
            }
        });
    };

    const cancel = () => {
        if (onCancel) onCancel();
    };

    if (errorGetData !== '') {
        return <div>{errorGetData}</div>;
    }

    return fetchingCreateRouteSegment ? (
        <LazyLoading height={300} />
    ) : (
        <>
            <div className="row">
                <div className="col-12">
                    <RouteSegmentForm
                        routeSegmentForm={routeSegment}
                        setRouteSegmentForm={setRouteSegment}
                        currencies={currencies}
                        units={units}
                        origins={origins}
                        destinations={destinations}
                        tolls={tolls}
                        errorFields={errorFields}
                    />
                </div>
            </div>
            <hr />
            <div className="row justify-content-end">
                <div className="col-auto">
                    <ButtonCancelForm callbackCancel={cancel} locked={fetchingStoreRouteSegment} />
                </div>
                <div className="col-auto">
                    <ButtonSaveForm callbackSave={store} locked={fetchingStoreRouteSegment} />
                </div>
            </div>
        </>
    );
};

export default RouteSegmentCreate;
